import { find, get } from "lodash";
import moment from "moment";
import {
  CONTACT_POINT_SYSTEM,
  INSURANCE_PLAN_PARAMS_RULES,
} from "utils/constants";
import { getDepartmentFromZipCode } from "utils/helpers/address";
import { buildInsurancePlanRule } from "utils/helpers/graphql";

const telecomValue = (project, system) =>
  String(
    get(find(get(project, "contact.telecoms") || [], { system }), "value") ||
      "",
  ).trim();

export const resolveProjectAge = (project) => {
  const birthDate = get(project, "contact.user.birthDate");
  if (!birthDate) return null;
  const birth = moment(birthDate);
  if (!birth.isValid()) return null;
  return moment().diff(birth, "years");
};

const issuanceRules = (project) => {
  const issuanceRaw = get(project, "contract.issuanceDate");
  if (!issuanceRaw) return [];
  const issuanceDate = moment(issuanceRaw).startOf("day");
  if (!issuanceDate.isValid()) return [];
  const today = moment().startOf("day");
  const daysUntilIssuance = issuanceDate.diff(today, "days");
  const [startRule, endRule] = get(project, "ria")
    ? [
        INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
        INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
      ]
    : [
        INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
        INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
      ];
  return [
    get(project, "retroactive")
      ? buildInsurancePlanRule({
          rule: INSURANCE_PLAN_PARAMS_RULES.RETROACTIVITY_ENABLED,
          predicate: "truthy",
        })
      : buildInsurancePlanRule({
          rule: startRule,
          predicate: "numericLte",
          value: daysUntilIssuance,
        }),
    buildInsurancePlanRule({
      rule: endRule,
      predicate:
        issuanceDate.year() === today.year()
          ? "numericGteOrZero"
          : "numericGte",
      value: daysUntilIssuance,
    }),
  ];
};

const includes = (rule, value) =>
  buildInsurancePlanRule({
    rule,
    predicate: "jsonbArrayIncludes",
    value: typeof value === "string" ? value.trim() : value,
  });

export const buildPlanEligibilityRules = (project, processId) => {
  const age = resolveProjectAge(project);
  const zipCode = telecomValue(project, CONTACT_POINT_SYSTEM.ZIP_CODE);
  return [
    ...issuanceRules(project),
    buildInsurancePlanRule({
      rule: INSURANCE_PLAN_PARAMS_RULES.MIN_AGE,
      predicate: "numericLte",
      value: age,
    }),
    buildInsurancePlanRule({
      rule: INSURANCE_PLAN_PARAMS_RULES.MAX_AGE,
      predicate: "numericGte",
      value: age,
    }),
    includes(
      INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES,
      get(project, "contact.socialRegime"),
    ),
    buildInsurancePlanRule({
      rule: INSURANCE_PLAN_PARAMS_RULES.EXERCISE_FRAME,
      predicate: "stringEq",
      value: get(project, "contact.exerciseFrame"),
    }),
    includes(
      INSURANCE_PLAN_PARAMS_RULES.PROFESSION,
      get(project, "contact.profession"),
    ),
    includes(
      INSURANCE_PLAN_PARAMS_RULES.PROFILES,
      get(project, "contact.fields.profile"),
    ),
    includes(
      INSURANCE_PLAN_PARAMS_RULES.SUB_PROFILES,
      get(project, "contact.fields.subProfile"),
    ),
    includes(
      INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS,
      getDepartmentFromZipCode(zipCode),
    ),
    includes(INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES, zipCode),
    includes(
      INSURANCE_PLAN_PARAMS_RULES.MUNICIPALITIES,
      telecomValue(project, CONTACT_POINT_SYSTEM.CITY),
    ),
    includes(INSURANCE_PLAN_PARAMS_RULES.PROCESSES, processId),
  ].filter(Boolean);
};
